/**
 * ListSelect: the one list filter of the analytics screen.
 *
 * It replaces a row of one button per list. Each button printed the full list
 * name with `whitespace-nowrap`, so a long import name ("Salon Vivatech 2026,
 * relance decideurs IDF") stretched its button past the card, and the row
 * scrolled sideways with its scrollbar hidden: lists past the edge of the
 * screen were simply invisible. A searchable single-choice combobox holds any
 * number of lists, any length of name, in a fixed width.
 *
 * Single choice, not multiple: everything downstream (completion tiles, the
 * export, the comparison period) reads ONE list or all of them.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ListChecks } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from '@/components/ui/command';
import { formatNumber, formatPercent } from '@/i18n/locale';

export interface ListCompletion {
  called: number;
  total: number;
  pct: number;
}

interface ListSelectProps {
  sources: string[];
  /** Completion per list, plus `__all` for the aggregate. */
  completion: Record<string, ListCompletion>;
  /** 'all' or a list name. */
  value: string;
  onChange: (value: string) => void;
}

const ALL = 'all';

function Progress({ comp }: { comp?: ListCompletion }) {
  const { t } = useTranslation(['dialer']);
  if (!comp || comp.total === 0) return null;
  return (
    <div className="flex items-center gap-2 w-full min-w-0">
      <div className="h-1 flex-1 rounded-full overflow-hidden" style={{ background: 'var(--vf-surface-3)' }}>
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, comp.pct)}%`, background: 'var(--vf-accent)' }} />
      </div>
      <span className="text-[11px] tabular-nums whitespace-nowrap" style={{ color: 'var(--vf-text-3)' }}>
        {t('dialer:analytics.listSelect.progress', {
          called: formatNumber(comp.called),
          total: formatNumber(comp.total),
          pct: formatPercent(comp.pct, 0),
        })}
      </span>
    </div>
  );
}

export default function ListSelect({ sources, completion, value, onChange }: ListSelectProps) {
  const { t } = useTranslation(['dialer']);
  const [open, setOpen] = useState(false);
  const allLabel = t('dialer:callHistory.filters.allLists');
  const selectedLabel = value === ALL ? allLabel : value;
  const selectedComp = completion[value === ALL ? '__all' : value];

  const items = useMemo(() => sources.filter(Boolean), [sources]);

  const pick = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={t('dialer:analytics.listSelect.ariaLabel')}
          title={selectedLabel}
          data-testid="analytics-list-select"
          className="inline-flex items-center gap-2 h-9 w-full sm:w-72 max-w-full min-w-0 px-3 rounded-md border border-input bg-transparent text-sm shadow-sm hover:bg-accent/50 transition-colors"
        >
          <ListChecks className="w-4 h-4 shrink-0" style={{ color: 'var(--vf-text-3)' }} />
          <span className="flex-1 min-w-0 truncate text-left font-medium">{selectedLabel}</span>
          {selectedComp && selectedComp.total > 0 && (
            <span className="text-xs tabular-nums shrink-0" style={{ color: 'var(--vf-text-3)' }}>
              {formatPercent(selectedComp.pct, 0)}
            </span>
          )}
          <ChevronDown className="w-4 h-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[min(22rem,calc(100vw-2rem))]">
        <Command>
          <CommandInput placeholder={t('dialer:analytics.listSelect.searchPlaceholder')} />
          <CommandList className="max-h-80">
            <CommandEmpty>{t('dialer:callHistory.filters.noListsFound')}</CommandEmpty>
            <CommandGroup>
              {/* i18n-exempt: a search value, not text */}
              <CommandItem value={`__all ${allLabel}`} onSelect={() => pick(ALL)} className="flex-col items-start gap-1">
                <div className="flex items-center gap-2 w-full min-w-0">
                  <span className="flex-1 min-w-0 truncate font-medium">{allLabel}</span>
                  {value === ALL && <Check className="w-4 h-4 shrink-0" style={{ color: 'var(--vf-accent)' }} />}
                </div>
                <Progress comp={completion.__all} />
              </CommandItem>
            </CommandGroup>
            {items.length > 0 && <CommandSeparator />}
            <CommandGroup>
              {items.map((src) => (
                <CommandItem
                  key={src}
                  value={src}
                  onSelect={() => pick(src)}
                  className="flex-col items-start gap-1"
                  title={src}
                >
                  <div className="flex items-center gap-2 w-full min-w-0">
                    <span className="flex-1 min-w-0 truncate">{src}</span>
                    {value === src && <Check className="w-4 h-4 shrink-0" style={{ color: 'var(--vf-accent)' }} />}
                  </div>
                  <Progress comp={completion[src]} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
